import { api } from '../../lib/apiClient';
import { uploadDirect } from '../../lib/directUpload';
import { useAuthStore } from '../../store/authStore';

export const fileKeys = {
  contents: (parentId, params) => ['folder-contents', parentId ?? 'root', params ?? {}],
  file: (id) => ['file', id],
  details: (id) => ['file-details', id],
  trash: (params) => ['trash-files', params ?? {}],
  shares: (params) => ['shares', params ?? {}],
  shareEvents: (id) => ['share-events', id],
  invitations: (resourceType, resourceId) => ['document-invitations', resourceType, resourceId],
  versions: (fileId) => ['file-versions', fileId],
  favorites: () => ['favorites'],
};

/**
 * Base path for the active workspace. Files and folders are workspace-scoped, so
 * every request is prefixed with /workspaces/:id. Pages that call these are
 * gated on `useTenant().ready`, so a workspace is always selected here.
 */
function ws() {
  const workspaceId = useAuthStore.getState().workspaceId;
  if (!workspaceId) throw new Error('No active workspace selected');
  return `/workspaces/${workspaceId}`;
}

// ---- Browsing ----
export async function getContents(parentId, params = {}) {
  const { data } = await api.get(`${ws()}/folders/contents`, {
    params: { ...(parentId ? { parentId } : {}), ...params },
  });
  return data;
}

// ---- Folders ----
export async function createFolder({ name, parentId }) {
  const { data } = await api.post(`${ws()}/folders`, { name, parentId });
  return data;
}
export async function renameFolder({ id, name }) {
  const { data } = await api.patch(`${ws()}/folders/${id}/rename`, { name });
  return data;
}
export async function moveFolder({ id, parentId }) {
  const { data } = await api.patch(`${ws()}/folders/${id}/move`, { parentId });
  return data;
}
export const trashFolder = (id) => api.delete(`${ws()}/folders/${id}`);
export const restoreFolder = (id) => api.post(`${ws()}/folders/${id}/restore`);
export const deleteFolderForever = (id) => api.delete(`${ws()}/folders/${id}/permanent`);

// ---- Files ----
/** File metadata (name, size, mimeType, folderId, …) for the full-screen viewer. */
export async function getFile(id) {
  const { data } = await api.get(`${ws()}/files/${id}`);
  return data;
}

/**
 * Everything the Details panel shows: the file's own facts plus the joins the
 * listing payload deliberately omits — owner/editor names, version tally,
 * sharing state, folder path. Fetched only when the panel opens.
 */
export async function getFileDetails(id) {
  const { data } = await api.get(`${ws()}/files/${id}/details`);
  return data;
}

/** Upload a new file browser → S3 in parts; `onProgress(percent)` as bytes go up. */
export function uploadFile({ file, folderId, onProgress, signal }) {
  return uploadDirect({
    file,
    onProgress,
    signal,
    begin: async () => {
      const { data } = await api.post(`${ws()}/files/uploads`, {
        name: file.name,
        size: file.size,
        mimeType: file.type || undefined,
        folderId: folderId || undefined,
      });
      return data;
    },
  });
}
export async function renameFile({ id, name }) {
  const { data } = await api.patch(`${ws()}/files/${id}/rename`, { name });
  return data;
}
export async function updateFileMetadata({ id, metadata }) {
  const { data } = await api.patch(`${ws()}/files/${id}/metadata`, metadata);
  return data;
}
export async function moveFile({ id, folderId }) {
  const { data } = await api.patch(`${ws()}/files/${id}/move`, { folderId });
  return data;
}
export const trashFile = (id) => api.delete(`${ws()}/files/${id}`);
export const restoreFile = (id) => api.post(`${ws()}/files/${id}/restore`);
export const deleteFileForever = (id) => api.delete(`${ws()}/files/${id}/permanent`);

export async function listTrashFiles(params = {}) {
  const { data } = await api.get(`${ws()}/files/trash`, { params });
  return data;
}

export async function listTrashFolders() {
  const { data } = await api.get(`${ws()}/folders/trash`);
  return data;
}

/** Download a file's bytes through the API and save it client-side. */
export async function downloadFile(id, name) {
  const res = await api.get(`${ws()}/files/${id}/content`, { responseType: 'blob' });
  triggerBlobDownload(res.data, name);
}

/**
 * Fetch a file's bytes as a Blob for in-app preview (Quick View). Authenticated
 * via axios, so it works for both the S3 and local storage drivers.
 */
export async function fetchFileBlob(id) {
  const res = await api.get(`${ws()}/files/${id}/preview`, { responseType: 'blob' });
  return res.data;
}

/** Presigned inline (preview) URL — S3 driver only; null on local driver. */
export async function getPreviewUrl(id) {
  const { data } = await api.get(`${ws()}/files/${id}/preview-link`);
  return data; // { url, fileId, name, mimeType }
}

// ---- Versions ----
export async function listFileVersions(fileId) {
  const { data } = await api.get(`${ws()}/files/${fileId}/versions`);
  return data;
}

export function uploadFileVersion({ fileId, file, onProgress, signal }) {
  return uploadDirect({
    file,
    onProgress,
    signal,
    begin: async () => {
      const { data } = await api.post(`${ws()}/files/${fileId}/uploads`, {
        size: file.size,
        mimeType: file.type || undefined,
      });
      return data;
    },
  });
}

export const restoreFileVersion = ({ fileId, versionNo }) =>
  api.post(`${ws()}/files/${fileId}/versions/${versionNo}/restore`);

export async function downloadFileVersion(fileId, versionNo, name) {
  const res = await api.get(`${ws()}/files/${fileId}/versions/${versionNo}/content`, {
    responseType: 'blob',
  });
  triggerBlobDownload(res.data, name);
}

function triggerBlobDownload(blob, name) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// ---- Shares (workspace-scoped management; public resolution stays at /shares/public) ----
export async function createShare(payload) {
  const { data } = await api.post(`${ws()}/shares`, payload);
  return data; // includes the raw `token` once
}
export async function listShares(params = {}) {
  const { data } = await api.get(`${ws()}/shares`, { params });
  return data;
}
export const revokeShare = (id) => api.delete(`${ws()}/shares/${id}`);

export async function getShareEvents(id, params = {}) {
  const { data } = await api.get(`${ws()}/shares/${id}/events`, { params });
  return data;
}

// ---- Email invitations (identity-bound; distinct from the token links above) ----
export async function inviteByEmail(payload) {
  const { data } = await api.post(`${ws()}/shares/invitations`, payload);
  return data; // includes the raw `token` once
}
export async function listPeopleWithAccess(resourceType, resourceId) {
  const { data } = await api.get(`${ws()}/shares/${resourceType}/${resourceId}/invitations`);
  return data;
}
export async function updateInvitation({ id, ...patch }) {
  const { data } = await api.patch(`${ws()}/shares/invitations/${id}`, patch);
  return data;
}
export const revokeInvitation = (id) => api.delete(`${ws()}/shares/invitations/${id}`);

// ---- Favorites ----
export async function listFavorites() {
  const { data } = await api.get(`${ws()}/favorites`);
  return data; // [{ targetType, targetId, createdAt }]
}
export const addFavorite = ({ targetType, targetId }) =>
  api.post(`${ws()}/favorites`, { targetType, targetId });
export const removeFavorite = ({ targetType, targetId }) =>
  api.delete(`${ws()}/favorites/${targetType}/${targetId}`);
